import React from 'react';
import {CommandBarButton, IContextualMenuItem, SearchBox, getTheme} from '@fluentui/react';
import useSubOrganizationScope, {useSwitchSubOrganization} from '../../hooks/useSubOrganizationScope';

const ALL_KEY = 'all-organization';

/**
 * Global sub-organization (customer) scope picker, mounted in the nav bar next to the organization
 * switcher.
 *
 * While a sub-organization is selected every request carries `x-suborganization`, so search pricing,
 * installed extensions and new installs are all that customer's. That is a big enough change of what
 * the screen means that the active state is deliberately loud: a filled brand-colored pill naming the
 * customer, rather than another quiet header button.
 */
const SubOrganizationSwitcher: React.FC = () => {
    const scope = useSubOrganizationScope();
    const switchSubOrganization = useSwitchSubOrganization();
    const [search, setSearch] = React.useState('');
    const theme = getTheme();

    if (!scope.isAvailable) return null;

    const isSearchable = scope.subOrganizations.length > 5;
    const sorted = scope.subOrganizations
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name, undefined, {sensitivity: 'base'}));
    const filtered = isSearchable && search
        ? sorted.filter(s => s.name.toLowerCase().includes(search.toLowerCase()) || s.id.toLowerCase().includes(search.toLowerCase()))
        : sorted;

    const items: IContextualMenuItem[] = [
        {
            key: ALL_KEY,
            text: 'All (organization)',
            iconProps: {iconName: scope.isScoped ? undefined : 'CheckMark'},
            onClick: () => {
                switchSubOrganization(undefined);
                setSearch('');
            },
        },
        ...(filtered.length > 0
            ? filtered.map(subOrganization => ({
                key: subOrganization.id,
                text: subOrganization.name,
                iconProps: {iconName: scope.selectedId === subOrganization.id ? 'CheckMark' : undefined},
                onClick: () => {
                    switchSubOrganization(subOrganization.id);
                    setSearch('');
                },
            }))
            : [{key: 'no-results', text: 'No results found', disabled: true}]),
    ];

    return (
        <CommandBarButton
            ariaLabel={scope.isScoped ? `Scoped to ${scope.selectedName}. Change customer scope` : 'Customer scope'}
            title={scope.isScoped
                ? `Every screen is showing ${scope.selectedName}'s data. Choose "All (organization)" to go back to the whole organization.`
                : 'Scope the app to a single sub-organization'}
            text={scope.isScoped ? scope.selectedName : 'All customers'}
            iconProps={{iconName: scope.isScoped ? 'ContactInfo' : 'People'}}
            data-testid='sub-organization-switcher'
            styles={{
                root: {
                    height: '32px',
                    margin: '8px 4px',
                    padding: '0 10px',
                    borderRadius: '16px',
                    maxWidth: '260px',
                    backgroundColor: scope.isScoped ? theme.palette.themePrimary : theme.palette.neutralLighter,
                    color: scope.isScoped ? theme.palette.white : theme.palette.neutralPrimary,
                    fontWeight: scope.isScoped ? 600 : 400,
                },
                rootHovered: {
                    backgroundColor: scope.isScoped ? theme.palette.themeDarkAlt : theme.palette.neutralLight,
                    color: scope.isScoped ? theme.palette.white : theme.palette.neutralPrimary,
                },
                rootExpanded: {
                    backgroundColor: scope.isScoped ? theme.palette.themeDarkAlt : theme.palette.neutralLight,
                    color: scope.isScoped ? theme.palette.white : theme.palette.neutralPrimary,
                },
                rootPressed: {
                    backgroundColor: scope.isScoped ? theme.palette.themeDark : theme.palette.neutralLight,
                    color: scope.isScoped ? theme.palette.white : theme.palette.neutralPrimary,
                },
                icon: {color: scope.isScoped ? theme.palette.white : theme.palette.neutralPrimary},
                iconExpanded: {color: scope.isScoped ? theme.palette.white : theme.palette.neutralPrimary},
                menuIcon: {color: scope.isScoped ? theme.palette.white : theme.palette.neutralPrimary},
                menuIconExpanded: {color: scope.isScoped ? theme.palette.white : theme.palette.neutralPrimary},
                label: {overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'},
            }}
            menuProps={{
                items,
                ...(isSearchable ? {
                    onRenderMenuList: (props: any, defaultRender: any) => (
                        <>
                            <SearchBox
                                placeholder='Search customers...'
                                value={search}
                                onChange={(_, v) => setSearch(v ?? '')}
                                styles={{root: {margin: '4px 8px 8px 8px'}}}
                            />
                            {defaultRender?.(props)}
                        </>
                    ),
                } : {}),
            }}
        />
    );
};

export default SubOrganizationSwitcher;
